import { toElements, type TElementSpecifier } from "@de-xima/xima-common-js-dom";

const InvokeFinishOnceAfterLastBeginTimes = new WeakMap<object, ReturnType<typeof setTimeout>>();

/**
 * Invokes the start function immediately and the end function after the specified delay.
 * When called again with the same ID before the delay has passed, the previous end invocation
 * is cancelled and rescheduled.
 *
 * @param id Identifier to group begin/end calls
 * @param delay Delay in milliseconds
 * @param start The function to invoke immediately.
 * @param end The function to invoke after the delay.
 */
export function invokeFinishOnceAfterLastBegin(id: object, delay: number, start: () => void, end: () => void): void {
  const oldTimer = InvokeFinishOnceAfterLastBeginTimes.get(id);
  InvokeFinishOnceAfterLastBeginTimes.delete(id);
  if (oldTimer !== undefined) {
    clearTimeout(oldTimer);
  }
  start();
  const newTimer = setTimeout(() => {
    InvokeFinishOnceAfterLastBeginTimes.delete(id);
    end();
  }, delay);
  InvokeFinishOnceAfterLastBeginTimes.set(id, newTimer);
}

/**
 * Adds a class to all elements matching the selector for the specified duration. After the duration has elapsed,
 * the class is removed.
 *
 * If this function is called again on the same element before the duration has elapsed, the removal of the class
 * is cancelled and rescheduled to occur after the new duration.
 *
 * @param elements Elements or CSS selector to which the class should be added
 * @param className Class name to add
 * @param duration Duration in milliseconds for which the class should be present.
 */
export function addClassForDuration(
  elements: TElementSpecifier<HTMLElement>,
  className: string,
  duration: number,
): void {
  for (const element of toElements(elements)) {
    invokeFinishOnceAfterLastBegin(
      element,
      duration,
      () => element.classList.add(className),
      () => element.classList.remove(className),
    );
  }
}
